export interface EidTagData {
  countryCode: string;
  flockNumber: string;
  animalNumber: string;
}

/** Accept complete records only. The transport owns framing. */
export function parseEidRecord(record: string): EidTagData | null {
  let digits = record.trim();
  if (digits.startsWith("[") && digits.endsWith("]")) digits = digits.slice(1, -1);
  digits = digits.replace(/^100[024]000(?=\d{15}$)/, "");
  digits = digits.replace(/^UK\s*/, "826").replace(/^XI\s*/, "899");
  if (!/^\d{3}(?:\s?\d){11,12}$/.test(digits)) return null;
  digits = digits.replace(/\s/g, "");
  if (digits.length === 14 && digits.startsWith("826")) digits = `8260${digits.slice(3)}`;
  if (digits.length !== 15 || /^0+$/.test(digits)) return null;
  const countryCode = digits.slice(0, 3);
  const national = digits.slice(3);
  // UK sheep display omits the ISO padding, but eidKey restores the full identity.
  const display = countryCode === "826" && national.startsWith("0") ? national.slice(1) : national;
  return { countryCode, flockNumber: display.slice(0, 6), animalNumber: display.slice(6) };
}

export function eidKey(eid: EidTagData): string {
  return `${eid.countryCode}${(eid.flockNumber + eid.animalNumber).padStart(12, "0")}`;
}

export function matchesEid(
  animalEid: { countryCodeNumber: string; flockNumber: string; animalNumber: string } | null | undefined,
  scan: EidTagData,
): boolean {
  if (!animalEid) return false;
  return eidKey({ ...animalEid, countryCode: animalEid.countryCodeNumber }) === eidKey(scan);
}

/** Serial text records end with CR, LF or ETX, independently of notification boundaries. */
export class TextEidFramer {
  private buffer = "";
  private discarding = false;

  constructor(private readonly onRejected: () => void = () => {}) {}

  reset(): void {
    this.buffer = "";
    this.discarding = false;
  }

  push(value: DataView): EidTagData[] {
    const result: EidTagData[] = [];
    for (let i = 0; i < value.byteLength; i++) {
      const byte = value.getUint8(i);
      if (byte === 13 || byte === 10 || byte === 3) {
        const tag = this.discarding ? null : parseEidRecord(this.buffer);
        if (tag) result.push(tag);
        else if (this.discarding || this.buffer.trim()) this.onRejected();
        this.reset();
      } else if (byte === 2 && !this.buffer) {
        continue;
      } else if (!this.discarding) {
        if (byte < 32 || byte > 126 || this.buffer.length >= 64) {
          this.buffer = "";
          this.discarding = true;
        } else {
          this.buffer += String.fromCharCode(byte);
        }
      }
    }
    return result;
  }
}

/** Halo+ alert payload: category 0, count 1, then 15 ASCII digits (firmware 8.6). */
export class HaloEidFramer {
  private bytes: number[] = [];

  constructor(private readonly onRejected: () => void = () => {}) {}

  reset(): void {
    this.bytes = [];
  }

  push(value: DataView): EidTagData[] {
    const tags: EidTagData[] = [];
    let rejected = false;
    for (let index = 0; index < value.byteLength; index++) {
      const byte = value.getUint8(index);
      if (byte === 0) {
        rejected ||= this.bytes.length > 0;
        this.bytes = [0];
        continue;
      }
      if (this.bytes.length === 1 && byte === 1) {
        this.bytes.push(byte);
      } else if (this.bytes.length >= 2 && byte >= 48 && byte <= 57) {
        this.bytes.push(byte);
        if (this.bytes.length === 17) {
          const tag = parseEidRecord(String.fromCharCode(...this.bytes.slice(2)));
          if (tag) tags.push(tag);
          else rejected = true;
          this.reset();
        }
      } else {
        rejected = true;
        this.reset();
      }
    }
    if (rejected) this.onRejected();
    return tags;
  }
}

/** HR0 decoding is limited to complete ten-byte notifications until framing is captured. */
export class Hr0EidFramer {
  reset(): void {}

  push(value: DataView): EidTagData[] {
    // Joining an interrupted binary record to the next one can fabricate a valid-looking EID.
    if (value.byteLength !== 10 || value.getUint8(0) !== 0x25) throw new Error("hr0_ambiguous_frame");
    const country = (value.getUint8(4) << 2) | (value.getUint8(5) >> 6);
    let national = BigInt(value.getUint8(5) & 0x3f);
    for (let i = 6; i < 10; i++) national = (national << 8n) | BigInt(value.getUint8(i));
    if (country > 999) throw new Error("hr0_invalid_country");
    const tag = parseEidRecord(`${String(country).padStart(3, "0")}${String(national).padStart(12, "0")}`);
    return tag ? [tag] : [];
  }
}
